'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const CommunityMap = dynamic(() => import('@/components/CommunityMap'), { ssr: false, loading: () => (
  <div className="h-96 rounded-2xl bg-navy-mid border border-white/10 flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-2" />
      <p className="text-snow/40 text-sm">Loading map…</p>
    </div>
  </div>
)});

interface DiscoveredPlace {
  id: string;
  title: string;
  description: string;
  category: string;
  province: string;
  district: string;
  difficulty: string;
  best_season: string;
  likes_count: number;
  comments_count: number;
  average_rating: number;
  confirmations_count: number;
  cover_photo_url: string | null;
  latitude: number | null;
  longitude: number | null;
  is_sensitive_location: boolean;
  approximate_location: boolean;
  created_at: string;
  submitted_by: string;
  submitter?: { full_name: string };
  distance?: number;
  is_demo?: boolean;
}

const CATEGORIES = [
  { value: '', label: 'All', icon: '🗺️' },
  { value: 'viewpoint', label: 'Viewpoint', icon: '🏔️' },
  { value: 'waterfall', label: 'Waterfall', icon: '💧' },
  { value: 'lake', label: 'Lake', icon: '🏞️' },
  { value: 'cave', label: 'Cave', icon: '🕳️' },
  { value: 'village', label: 'Village', icon: '🏘️' },
  { value: 'temple', label: 'Temple', icon: '🛕' },
  { value: 'trekking_route', label: 'Trekking', icon: '🥾' },
  { value: 'forest', label: 'Forest', icon: '🌲' },
  { value: 'picnic_spot', label: 'Picnic', icon: '🧺' },
  { value: 'cultural_site', label: 'Cultural', icon: '🏛️' },
  { value: 'hidden_place', label: 'Hidden', icon: '🔍' },
  { value: 'other', label: 'Other', icon: '📍' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: 'text-green-400 bg-green-400/10',
  moderate: 'text-gold bg-gold/10',
  hard: 'text-orange-400 bg-orange-400/10',
  expert: 'text-red-400 bg-red-400/10',
};

type SortOption = 'newest' | 'popular' | 'top_rated' | 'nearby' | 'most_confirmed';

// Demo fallback places — shown when discovered_places table is empty
const DEMO_PLACES: DiscoveredPlace[] = [
  { id: 'demo-place-1', title: 'Devi\'s Fall Secret Viewpoint', description: 'A hidden viewpoint above Devi\'s Fall with panoramic Annapurna views rarely visited by tourists.', category: 'viewpoint', province: 'Gandaki', district: 'Kaski', difficulty: 'easy', best_season: 'Autumn', likes_count: 47, comments_count: 12, average_rating: 4.8, confirmations_count: 23, cover_photo_url: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=600&q=80', latitude: 28.1900, longitude: 83.9600, is_sensitive_location: false, approximate_location: false, created_at: new Date().toISOString(), submitted_by: '', submitter: { full_name: 'Hari Thapa' }, is_demo: true },
  { id: 'demo-place-2', title: 'Gosaikunda Hidden Trail', description: 'A lesser-known trail to Gosaikunda lake bypassing the main route — serene and uncrowded.', category: 'lake', province: 'Bagmati', district: 'Rasuwa', difficulty: 'moderate', best_season: 'Spring', likes_count: 89, comments_count: 31, average_rating: 4.9, confirmations_count: 41, cover_photo_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80', latitude: 28.0900, longitude: 85.4200, is_sensitive_location: false, approximate_location: true, created_at: new Date().toISOString(), submitted_by: '', submitter: { full_name: 'Sita Lama' }, is_demo: true },
  { id: 'demo-place-3', title: 'Mustang Cave Monastery', description: 'Ancient cave monastery carved into the cliffs of Upper Mustang — rarely documented.', category: 'cave', province: 'Gandaki', district: 'Mustang', difficulty: 'hard', best_season: 'Summer', likes_count: 134, comments_count: 45, average_rating: 4.95, confirmations_count: 67, cover_photo_url: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80', latitude: 29.1800, longitude: 83.9700, is_sensitive_location: false, approximate_location: true, created_at: new Date().toISOString(), submitted_by: '', submitter: { full_name: 'Karma Sherpa' }, is_demo: true },
  { id: 'demo-place-4', title: 'Chitwan Jungle Waterfall', description: 'A secret waterfall deep inside Chitwan National Park accessible only by foot.', category: 'waterfall', province: 'Bagmati', district: 'Chitwan', difficulty: 'moderate', best_season: 'Monsoon', likes_count: 62, comments_count: 18, average_rating: 4.7, confirmations_count: 29, cover_photo_url: 'https://images.unsplash.com/photo-1549366021-9f761d450615?w=600&q=80', latitude: 27.5100, longitude: 84.3300, is_sensitive_location: false, approximate_location: false, created_at: new Date().toISOString(), submitted_by: '', submitter: { full_name: 'Bikash Rai' }, is_demo: true },
  { id: 'demo-place-5', title: 'Rara Lake Sunset Point', description: 'The best sunset viewpoint at Rara Lake, known only to local fishermen.', category: 'viewpoint', province: 'Karnali', district: 'Mugu', difficulty: 'easy', best_season: 'Autumn', likes_count: 201, comments_count: 78, average_rating: 5.0, confirmations_count: 112, cover_photo_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80', latitude: 29.5200, longitude: 82.0800, is_sensitive_location: false, approximate_location: false, created_at: new Date().toISOString(), submitted_by: '', submitter: { full_name: 'Maya Gurung' }, is_demo: true },
  { id: 'demo-place-6', title: 'Bandipur Forest Temple', description: 'A 300-year-old forest temple hidden in the hills above Bandipur bazaar.', category: 'temple', province: 'Gandaki', district: 'Tanahun', difficulty: 'easy', best_season: 'Winter', likes_count: 38, comments_count: 9, average_rating: 4.6, confirmations_count: 15, cover_photo_url: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=600&q=80', latitude: 27.9400, longitude: 84.4100, is_sensitive_location: false, approximate_location: false, created_at: new Date().toISOString(), submitted_by: '', submitter: { full_name: 'Priya Shrestha' }, is_demo: true },
];

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)}km`;
}

export default function DiscoverPage() {
  const { user } = useAuth();
  const [places, setPlaces] = useState<DiscoveredPlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [userLikes, setUserLikes] = useState<Set<string>>(new Set());
  const [savedPlaces, setSavedPlaces] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const [shareToast, setShareToast] = useState('');
  const [usingDemoData, setUsingDemoData] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    fetchPlaces();
  }, [selectedCategory, sortBy]);

  useEffect(() => {
    if (user) fetchUserLikes();
    try {
      const saved = localStorage.getItem('savedPlaces');
      if (saved) setSavedPlaces(new Set(JSON.parse(saved)));
    } catch {}
  }, [user]);

  const fetchPlaces = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('discovered_places')
        .select('*, submitter:users!discovered_places_submitted_by_fkey(full_name)')
        .eq('status', 'published');

      if (selectedCategory) query = query.eq('category', selectedCategory);

      switch (sortBy) {
        case 'popular': query = query.order('likes_count', { ascending: false }); break;
        case 'top_rated': query = query.order('average_rating', { ascending: false }); break;
        case 'most_confirmed': query = query.order('confirmations_count', { ascending: false }); break;
        default: query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.warn('[YatraNest Discover] Query failed:', error.message);
        loadDemoFallback();
        return;
      }

      if (!data || data.length === 0) {
        loadDemoFallback();
      } else {
        setPlaces(data);
        setUsingDemoData(false);
      }
    } catch (err) {
      console.warn('[YatraNest Discover] Unexpected error:', (err as any)?.message);
      loadDemoFallback();
    }
    setLoading(false);
  };

  const loadDemoFallback = () => {
    let demo = [...DEMO_PLACES];
    if (selectedCategory) demo = demo.filter(p => p.category === selectedCategory);
    if (sortBy === 'popular') demo.sort((a, b) => b.likes_count - a.likes_count);
    else if (sortBy === 'top_rated') demo.sort((a, b) => b.average_rating - a.average_rating);
    else if (sortBy === 'most_confirmed') demo.sort((a, b) => b.confirmations_count - a.confirmations_count);
    setPlaces(demo);
    setUsingDemoData(true);
    setLoading(false);
  };

  const fetchUserLikes = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('discovered_place_likes')
        .select('place_id')
        .eq('user_id', user.id);
      if (data) setUserLikes(new Set(data.map((l: any) => l.place_id)));
    } catch {}
  };

  const toggleLike = async (placeId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    // Don't allow likes on demo data
    const place = places.find(p => p.id === placeId);
    if (place?.is_demo) return;

    const isLiked = userLikes.has(placeId);
    if (isLiked) {
      await supabase.from('discovered_place_likes').delete().eq('place_id', placeId).eq('user_id', user.id);
      setUserLikes(prev => { const n = new Set(prev); n.delete(placeId); return n; });
      setPlaces(prev => prev.map(p => p.id === placeId ? { ...p, likes_count: Math.max(0, p.likes_count - 1) } : p));
    } else {
      await supabase.from('discovered_place_likes').insert({ place_id: placeId, user_id: user.id });
      setUserLikes(prev => new Set([...prev, placeId]));
      setPlaces(prev => prev.map(p => p.id === placeId ? { ...p, likes_count: p.likes_count + 1 } : p));
    }
  };

  const toggleSave = (placeId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSavedPlaces(prev => {
      const n = new Set(prev);
      if (n.has(placeId)) n.delete(placeId); else n.add(placeId);
      try { localStorage.setItem('savedPlaces', JSON.stringify([...n])); } catch {}
      return n;
    });
  };

  const sharePlace = async (place: DiscoveredPlace, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/discover/${place.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: place.title, text: `Check out ${place.title} on YatraNest!`, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); } catch {}
      setShareToast(`Link copied for "${place.title}"`);
      setTimeout(() => setShareToast(''), 2500);
    }
  };

  const getNearbyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      return;
    }
    setLocationLoading(true);
    setLocationDenied(false);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSortBy('nearby');
        setLocationLoading(false);
      },
      () => {
        setLocationLoading(false);
        setLocationDenied(true);
      },
      { timeout: 8000 }
    );
  }, []);

  const placesWithDistance = places.map(p => ({
    ...p,
    distance: userLocation && p.latitude && p.longitude
      ? haversineKm(userLocation.lat, userLocation.lng, Number(p.latitude), Number(p.longitude))
      : undefined,
  }));

  const filtered = placesWithDistance.filter(p =>
    !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.district?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.province?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'nearby' && a.distance !== undefined && b.distance !== undefined) {
      return a.distance - b.distance;
    }
    return 0;
  });

  // Never expose sensitive GPS coordinates publicly
  const mapPlaces = sorted.filter(p => p.latitude && p.longitude && !p.is_sensitive_location && !p.is_demo);
  const getCategoryIcon = (cat: string) => CATEGORIES.find(c => c.value === cat)?.icon || '📍';

  return (
    <main className="min-h-screen bg-navy">
      <Header />
      <div className="pt-20">
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-to-b from-navy-mid to-navy py-14 px-4">
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-gold blur-3xl" />
            <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-sky blur-3xl" />
          </div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 rounded-full px-4 py-1.5 text-gold text-sm mb-4">
              <span>🔍</span> Community Discovered
            </div>
            <h1 className="text-snow font-bold text-4xl md:text-5xl mb-4">Discover Hidden Nepal</h1>
            <p className="text-snow/60 text-lg max-w-2xl mx-auto mb-6">
              Explore secret places discovered by fellow travellers — waterfalls, viewpoints, caves, and hidden gems.
            </p>

            {/* Demo data notice */}
            {usingDemoData && (
              <div className="mb-4 mx-auto max-w-xl px-4 py-2.5 rounded-xl bg-gold/10 border border-gold/20 text-gold/80 text-xs flex items-center gap-2 text-left">
                <span>ℹ️</span>
                <span>Showing demo places — be the first to submit a real hidden gem!</span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search places, districts, provinces..."
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-snow placeholder-snow/40 focus:outline-none focus:border-gold/50 text-sm pr-10"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-snow/30 text-sm">🔍</span>
              </div>
              {user && (
                <Link href="/discover/submit" className="flex items-center justify-center gap-2 px-6 py-3 bg-gold text-navy font-semibold rounded-xl hover:bg-gold/90 transition-colors text-sm whitespace-nowrap">
                  <span>+</span> Add a Place
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Quick links bar */}
        <div className="bg-navy/95 border-b border-white/5 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              <Link href="/discover/leaderboard" className="flex items-center gap-1.5 text-xs text-snow/60 hover:text-gold transition-colors px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                🏆 Leaderboard
              </Link>
              {user && (
                <>
                  <Link href="/discover/my-contributions" className="flex items-center gap-1.5 text-xs text-snow/60 hover:text-gold transition-colors px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                    📋 My Places
                  </Link>
                  <Link href="/discover/profile" className="flex items-center gap-1.5 text-xs text-snow/60 hover:text-gold transition-colors px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                    🧭 My Profile
                  </Link>
                </>
              )}
              {savedPlaces.size > 0 && (
                <Link href="/discover/saved" className="flex items-center gap-1.5 text-xs text-snow/60 hover:text-gold transition-colors px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  🔖 Saved ({savedPlaces.size})
                </Link>
              )}
            </div>
            <div className="flex gap-1 bg-white/5 border border-white/10 rounded-xl p-1">
              <button onClick={() => setViewMode('grid')} className={`px-3 py-1 rounded-lg text-xs transition-colors ${viewMode === 'grid' ? 'bg-gold/20 text-gold' : 'text-snow/50 hover:text-snow'}`}>⊞ Grid</button>
              <button onClick={() => setViewMode('map')} className={`px-3 py-1 rounded-lg text-xs transition-colors ${viewMode === 'map' ? 'bg-gold/20 text-gold' : 'text-snow/50 hover:text-snow'}`}>🗺️ Map</button>
            </div>
          </div>
        </div>

        {/* Category filter */}
        <div className="sticky top-16 z-20 bg-navy/95 backdrop-blur border-b border-white/5 px-4 py-2">
          <div className="max-w-7xl mx-auto overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 min-w-max">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === cat.value ? 'bg-gold/20 text-gold border border-gold/30' : 'bg-white/5 text-snow/60 hover:text-snow border border-white/10'
                  }`}
                >
                  <span>{cat.icon}</span>{cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Sort + Nearby bar */}
          <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-snow/40 text-xs">Sort:</span>
              {([
                { key: 'newest', label: '🕐 Newest' },
                { key: 'popular', label: '❤️ Popular' },
                { key: 'top_rated', label: '⭐ Top Rated' },
                { key: 'most_confirmed', label: '✅ Confirmed' },
              ] as { key: SortOption; label: string }[]).map(s => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${sortBy === s.key ? 'bg-gold/20 text-gold border border-gold/30' : 'bg-white/5 text-snow/50 border border-white/10 hover:text-snow'}`}
                >
                  {s.label}
                </button>
              ))}
              <button
                onClick={getNearbyLocation}
                disabled={locationLoading}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors flex items-center gap-1 ${sortBy === 'nearby' ? 'bg-sky/20 text-sky border border-sky/30' : 'bg-white/5 text-snow/50 border border-white/10 hover:text-snow'}`}
              >
                {locationLoading ? <span className="w-3 h-3 border border-sky border-t-transparent rounded-full animate-spin" /> : '📍'}
                Nearby
              </button>
            </div>
            <p className="text-snow/30 text-xs">{sorted.length} places found</p>
          </div>

          {/* Location denied notice */}
          {locationDenied && (
            <div className="mb-4 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center gap-2">
              <span>📍</span>
              <span>Location access was denied. Please enable location permissions in your browser to use Nearby search.</span>
              <button onClick={() => setLocationDenied(false)} className="ml-auto text-red-400/60 hover:text-red-400">✕</button>
            </div>
          )}

          {/* Map View */}
          {viewMode === 'map' && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-snow font-semibold text-sm">🗺️ Interactive Map — {mapPlaces.length} places with GPS</h2>
                <p className="text-snow/40 text-xs hidden sm:block">Click markers to explore · Sensitive locations hidden for safety</p>
              </div>
              {mapPlaces.length > 0 ? (
                <CommunityMap
                  places={mapPlaces.map(p => ({
                    id: p.id, title: p.title,
                    latitude: p.latitude!, longitude: p.longitude!,
                    category: p.category, difficulty: p.difficulty,
                    cover_photo_url: p.cover_photo_url,
                    likes_count: p.likes_count, average_rating: p.average_rating,
                  }))}
                  height="500px"
                  zoom={7}
                />
              ) : (
                <div className="h-64 rounded-2xl bg-navy-mid border border-white/10 flex items-center justify-center">
                  <div className="text-center">
                    <span className="text-4xl mb-3 block">🗺️</span>
                    <p className="text-snow/40 text-sm">No places with GPS coordinates yet</p>
                    <p className="text-snow/30 text-xs mt-1">Submit a place with GPS to see it on the map</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <>
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} className="yn-card rounded-2xl overflow-hidden animate-pulse">
                      <div className="h-48 bg-white/5" />
                      <div className="p-4 space-y-2">
                        <div className="h-4 bg-white/5 rounded w-3/4" />
                        <div className="h-3 bg-white/5 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : sorted.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-5xl mb-4">🗺️</div>
                  <h3 className="text-snow font-bold text-xl mb-2">No places found</h3>
                  <p className="text-snow/40 text-sm mb-6">
                    {searchQuery ? 'Try a different search term.' : 'Be the first to discover and share a hidden gem!'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="px-5 py-2.5 bg-white/5 border border-white/10 text-snow/70 rounded-xl text-sm hover:border-gold/30 transition-colors">
                        Reset Filters
                      </button>
                    )}
                    {user && (
                      <Link href="/discover/submit" className="btn-gold px-6 py-2.5 rounded-xl text-sm">
                        Submit a Place
                      </Link>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sorted.map(place => (
                    <Link key={place.id} href={place.is_demo ? '/discover/submit' : `/discover/${place.id}`} className="yn-card rounded-2xl overflow-hidden hover:border-gold/30 transition-all group block">
                      {/* Cover image */}
                      <div className="relative h-48 bg-navy-mid overflow-hidden">
                        {place.cover_photo_url ? (
                          <img
                            src={place.cover_photo_url}
                            alt={`${place.title} — community discovered place in Nepal`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-6xl opacity-20">{getCategoryIcon(place.category)}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
                        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                          <span className="text-xs bg-navy/80 text-snow/80 px-2 py-1 rounded-full backdrop-blur">
                            {getCategoryIcon(place.category)} {place.category?.replace(/_/g, ' ')}
                          </span>
                          {place.is_demo && (
                            <span className="text-xs bg-gold/20 text-gold/80 px-2 py-1 rounded-full border border-gold/20">Demo</span>
                          )}
                        </div>
                        {place.difficulty && (
                          <div className="absolute top-3 right-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${DIFFICULTY_COLORS[place.difficulty] || 'text-snow/60 bg-white/10'}`}>
                              {place.difficulty}
                            </span>
                          </div>
                        )}
                        {/* Save + Share overlay buttons */}
                        <div className="absolute bottom-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={e => toggleSave(place.id, e)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm backdrop-blur transition-colors ${savedPlaces.has(place.id) ? 'bg-gold/80 text-navy' : 'bg-navy/70 text-snow/70 hover:bg-gold/60 hover:text-navy'}`}
                            title={savedPlaces.has(place.id) ? 'Unsave' : 'Save'}
                          >
                            🔖
                          </button>
                          <button
                            onClick={e => sharePlace(place, e)}
                            className="w-8 h-8 rounded-full bg-navy/70 text-snow/70 hover:bg-sky/60 hover:text-white flex items-center justify-center text-sm backdrop-blur transition-colors"
                            title="Share"
                          >
                            📤
                          </button>
                        </div>
                        <div className="absolute bottom-3 left-3 right-16">
                          <h3 className="text-snow font-bold text-base leading-tight line-clamp-1">{place.title}</h3>
                          {(place.district || place.province) && (
                            <p className="text-snow/60 text-xs mt-0.5">📍 {[place.district, place.province].filter(Boolean).join(', ')}</p>
                          )}
                        </div>
                      </div>

                      {/* Card body */}
                      <div className="p-4">
                        {place.description && (
                          <p className="text-snow/60 text-sm line-clamp-2 mb-3">{place.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mb-3 text-xs text-snow/50">
                          {place.best_season && <span>🌤️ {place.best_season}</span>}
                          {place.average_rating > 0 && <span>⭐ {Number(place.average_rating).toFixed(1)}</span>}
                          {place.confirmations_count > 0 && <span>✅ {place.confirmations_count}</span>}
                          {place.distance !== undefined && (
                            <span className="text-sky">📍 {formatDistance(place.distance)}</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-white/5">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={e => toggleLike(place.id, e)}
                              className={`flex items-center gap-1 text-xs transition-colors ${place.is_demo ? 'text-snow/30 cursor-default' : userLikes.has(place.id) ? 'text-red-400' : 'text-snow/40 hover:text-red-400'}`}
                              title={place.is_demo ? 'Demo content' : undefined}
                            >
                              {userLikes.has(place.id) ? '❤️' : '🤍'} {place.likes_count}
                            </button>
                            <span className="text-snow/30 text-xs">💬 {place.comments_count}</span>
                          </div>
                          <span className="text-snow/30 text-xs truncate max-w-[100px]">
                            by {place.submitter?.full_name || 'Explorer'}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* FAB for mobile */}
        {user && (
          <Link
            href="/discover/submit"
            className="fixed bottom-6 right-6 w-14 h-14 bg-gold text-navy rounded-full shadow-2xl flex items-center justify-center text-2xl font-bold hover:bg-gold/90 transition-colors z-30 lg:hidden"
            title="Add a new place"
          >
            +
          </Link>
        )}

        {/* Share toast */}
        {shareToast && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-navy-mid border border-white/20 text-snow text-sm px-5 py-3 rounded-xl shadow-xl z-50 whitespace-nowrap">
            ✅ {shareToast}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
